import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAdminProducerContext } from '@/context/AdminProducerContext';
import { ImagesSection } from '@/features/producer-editor';
import { useDraftSessionId } from '@/hooks/useDraftSessionId';

export default function AdminProducerBilder() {
  const { producerId } = useParams<{ producerId: string }>();
  const { getProducerById } = useAdminProducerContext();
  const [producer, setProducer] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const draftSessionId = useDraftSessionId(`admin-producer-media-${producerId ?? 'new'}`);

  useEffect(() => {
    const loadProducer = async () => {
      if (!producerId) return;
      setIsLoading(true);
      try {
        const data = await getProducerById(producerId);
        setProducer(data || null);
      } catch (err) {
        console.error('Failed to load producer:', err);
        setProducer(null);
      } finally {
        setIsLoading(false);
      }
    };
    loadProducer();
  }, [producerId, getProducerById]);

  if (isLoading) {
    return (
      <AdminLayout producerId={producerId}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (!producer) {
    return (
      <AdminLayout producerId={producerId}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Producenten hittades inte.</AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout producerId={producerId} producerName={producer.name} title="Bilder">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Bilder</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Omslagsbild och galleri. Bilder sparas automatiskt.
          </p>
        </div>
        <Badge variant={producer.status === 'public' ? 'default' : 'secondary'}>
          {producer.status === 'public' ? 'Publicerad' : 'Utkast'}
        </Badge>
      </div>

      <div className="max-w-2xl">
        <ImagesSection
          producerId={producerId}
          draftSessionId={draftSessionId}
        />
      </div>
    </AdminLayout>
  );
}
